import React from "react";
import PropTypes from "prop-types";
import { Chip, Step, StepLabel, Stepper } from "@mui/material";
import MDTypography from "components/MDTypography";
import colors from "styles/tokens/colors";

const STEPS = [
  { status: "pending_dns", label: "DNS records", timestamp: null },
  { status: "verified", label: "Ownership verified", timestamp: "verified_at" },
  { status: "tls_pending", label: "Securing with HTTPS", timestamp: null },
  { status: "active", label: "Live", timestamp: "activated_at" },
];

const STATUS_META = {
  none: { label: "Not connected", tone: "neutral" },
  pending_dns: { label: "Waiting for DNS", tone: "warning" },
  verified: { label: "Verified", tone: "info" },
  tls_pending: { label: "Securing HTTPS", tone: "info" },
  active: { label: "Live", tone: "success" },
  failed: { label: "Needs attention", tone: "error" },
  disabled: { label: "Disabled", tone: "neutral" },
};

export function domainStatusMeta(status) {
  return STATUS_META[status] || STATUS_META.none;
}

export function DomainStatusChip({ status, size }) {
  const meta = domainStatusMeta(status);
  const tone = colors.status[meta.tone];
  return (
    <Chip
      size={size}
      label={meta.label}
      sx={{ bgcolor: tone.background, color: tone.text, fontWeight: 600, "& .MuiChip-label": { px: 1.25 } }}
    />
  );
}

DomainStatusChip.propTypes = { status: PropTypes.string, size: PropTypes.string };
DomainStatusChip.defaultProps = { status: "none", size: "small" };

export function formatTimestamp(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

const TRACK = "#C9D3E3";

// The dashboard theme styles steppers for a coloured header (white labels and
// icons), which vanish on a white card. Restyle them for a light surface.
const STEPPER_SX = {
  background: "transparent",
  boxShadow: "none",
  p: 0,
  my: 2,
  "& .MuiStepLabel-label, & .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed": {
    color: `${colors.text.light} !important`,
    textTransform: "none",
  },
  "& .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed": {
    color: `${colors.text.dark} !important`,
    fontWeight: "600 !important",
  },
  "& .MuiStepLabel-label.Mui-error": { color: `${colors.error.text} !important` },
  "& .MuiStepIcon-root": { background: TRACK, fill: TRACK, stroke: TRACK, boxShadow: "none" },
  "& .MuiStepIcon-root.Mui-active": {
    background: colors.brand.primary, fill: colors.brand.primary, stroke: colors.brand.primary, boxShadow: "none",
  },
  "& .MuiStepIcon-root.Mui-completed": {
    background: colors.success.main, fill: colors.success.main, stroke: colors.success.main, boxShadow: "none",
  },
  "& .MuiStepIcon-root.Mui-error": {
    background: colors.error.text, fill: colors.error.text, stroke: colors.error.text, boxShadow: "none",
  },
  "& .MuiStepConnector-root": { color: TRACK },
  "& .MuiStepConnector-root.Mui-active, & .MuiStepConnector-root.Mui-completed": { color: colors.success.main },
  "& .MuiStepConnector-line": { opacity: 1 },
};

function DomainStatusStepper({ domain }) {
  const status = domain?.status || "none";
  const failed = status === "failed";
  const reached = STEPS.findIndex((step) => step.status === status);
  // A failed domain stalls at the first step that has not completed yet.
  const stalledAt = domain?.verified_at ? 2 : 0;
  const activeStep = status === "active" ? STEPS.length : (failed ? stalledAt : Math.max(reached, 0));

  return (
    <Stepper activeStep={activeStep} alternativeLabel sx={STEPPER_SX}>
      {STEPS.map((step, index) => {
        const stamp = step.timestamp ? formatTimestamp(domain?.[step.timestamp]) : "";
        const isError = failed && index === stalledAt;
        return (
          <Step key={step.status} completed={index < activeStep}>
            <StepLabel
              error={isError}
              optional={(
                <MDTypography variant="caption" color={isError ? "error" : "text"} display="block">
                  {isError ? "Stopped retrying" : stamp}
                </MDTypography>
              )}
            >
              {step.label}
            </StepLabel>
          </Step>
        );
      })}
    </Stepper>
  );
}

DomainStatusStepper.propTypes = {
  domain: PropTypes.shape({
    status: PropTypes.string,
    verified_at: PropTypes.string,
    activated_at: PropTypes.string,
  }),
};

DomainStatusStepper.defaultProps = { domain: null };

export default DomainStatusStepper;
